/**
 * Centralized formatting utilities
 * Pure functions for consistent data display across the application
 */

/**
 * Format numbers with K/M suffixes for large values
 * @param {number|string|null|undefined} value - Value to format
 * @returns {string|number} Formatted value
 */
export const formatNumber = (value) => {
  if (value === null || value === undefined) return 0;
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;
  if (numeric >= 1_000_000) return `${(numeric / 1_000_000).toFixed(1)}M`;
  if (numeric >= 1_000) return `${(numeric / 1_000).toFixed(1)}k`;
  return numeric;
};

/**
 * Format token counts with K/M suffixes, shows em-dash for null/undefined
 * @param {number|string|null|undefined} tokens - Token count
 * @returns {string} Formatted token count
 */
export const formatTokens = (tokens) => {
  if (tokens === null || tokens === undefined) return '—';
  if (!Number.isFinite(tokens)) return String(tokens);
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`;
  if (tokens >= 1_000) return `${(tokens / 1_000).toFixed(1)}k`;
  return Math.round(tokens).toLocaleString();
};

/**
 * Format count values with locale string, shows em-dash for null/undefined
 * @param {number|string|null|undefined} value - Count value
 * @returns {string} Formatted count
 */
export const formatCount = (value) => {
  if (value === null || value === undefined) return '—';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return numeric.toLocaleString();
};

/**
 * Format dates to locale string
 * @param {string|Date|null|undefined} value - Date value
 * @returns {string} Formatted date string
 */
export const formatDate = (value) => {
  if (!value) return 'N/A';
  try {
    return new Date(value).toLocaleString();
  } catch (err) {
    return value;
  }
};

/**
 * Format duration in milliseconds to human-readable string
 * @param {number} ms - Duration in milliseconds
 * @returns {string} Formatted duration
 */
export const formatDuration = (ms) => {
  if (ms === null || ms === undefined) return '—';
  const numeric = Number(ms);
  if (!Number.isFinite(numeric)) return String(ms);

  if (numeric >= 60_000) return `${(numeric / 60_000).toFixed(1)}m`;
  if (numeric >= 1_000) return `${(numeric / 1_000).toFixed(1)}s`;
  return `${Math.round(numeric)}ms`;
};

/**
 * Format duration in milliseconds to seconds with 2 decimal precision
 * @param {number} ms - Duration in milliseconds
 * @returns {string|number} Duration in seconds with 2 decimals
 */
export const formatDurationSeconds = (ms) => {
  if (ms === null || ms === undefined) return '—';
  const numeric = Number(ms);
  if (!Number.isFinite(numeric)) return String(ms);
  return (numeric / 1_000).toFixed(2);
};

/**
 * Format cost with appropriate decimal precision
 * TEMPORARY: Always using 4 decimals for debugging
 * @param {number} cost - Cost value
 * @returns {string} Formatted cost with $ sign
 */
export const formatCost = (cost) => {
  if (cost === null || cost === undefined) return '—';
  const numeric = Number(cost);
  if (!Number.isFinite(numeric)) return String(cost);

  // Special case: zero cost
  if (numeric === 0) return '$0';

  // TEMPORARY: Always use 4 decimals for debugging
  return `$${numeric.toFixed(4)}`;
};

/**
 * Format timestamp as relative time (e.g. "2h ago", "5d ago")
 * @param {string|Date|number|null|undefined} timestamp - Timestamp to format
 * @returns {string} Formatted relative time
 */
export const formatRelativeTime = (timestamp) => {
  if (!timestamp) return '—';
  const now = Date.now();
  const time = new Date(timestamp).getTime();
  const diff = now - time;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  return `${minutes}m ago`;
};

/**
 * Format timestamp to local time (e.g. "Dec 8, 6:06 PM")
 * @param {string|Date|number|null|undefined} timestamp - Timestamp to format
 * @returns {string} Formatted local time
 */
export const formatLocalTime = (timestamp) => {
  if (!timestamp) return '—';
  try {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch (err) {
    return '—';
  }
};
